import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { useCallback } from 'react';
import { taxInfoProp } from '../../types';
import {
  DEFAULT_LENGTH_VALUE,
  INVALID_NUM_TAX_FIELD_CHARS_REGEX,
  INVALID_TAX_FIELD_CHARS_REGEX,
  NAME_ERROR,
  ONLY_DIGIT_TAX_FIELD_CHARS_REGEX
} from '../../constants';
import { TaxModalInput } from '../tax-modal-input/tax-modal-input';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { getTaxIDValidation } from '../../utils';

export const CompanyTaxFields = ({
  countryPayU,
  companyLabel,
  isStateTaxIdEnabled,
  isStateTaxIdRequired,
  isNeighborhoodEnabled,
  isNeighborhoodRequired,
  stateTaxIdLabel,
  neighborhoodLabel
}: Readonly<taxInfoProp>) => {
  const t = useTranslations();

  const { watch, setValue } = useFormContext();

  const handleTaxIDValidation = useCallback(
    (value) => getTaxIDValidation(value, countryPayU, t),
    [countryPayU, t]
  );

  const handleKeyDown = (e) => {
    if (INVALID_NUM_TAX_FIELD_CHARS_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const sanitizedData = pastedData.replace(
      ONLY_DIGIT_TAX_FIELD_CHARS_REGEX,
      ''
    );
    setValue('stateTaxId', `${watch('stateTaxId')} ${sanitizedData}`);
  };

  return (
    <>
      <TaxModalInput
        label={companyLabel}
        name="taxId"
        hideWordCount={true}
        type="text"
        data-testid="companyTaxId"
        maxlength={DEFAULT_LENGTH_VALUE}
        className="mb-3"
        required
        rules={{
          required: {
            value: true,
            message: t('ERROR_CAT_TAX_ID_COMPANY_REQUIRED')
          },
          pattern: {
            value: INVALID_TAX_FIELD_CHARS_REGEX,
            message: replaceTokensInString(
              t('ADD_INV_CHAR_NEW_FIELD_WARNING', {
                1: NAME_ERROR
              })
            )
          },
          validate: handleTaxIDValidation
        }}
      />
      {isStateTaxIdEnabled && (
        <TaxModalInput
          label={stateTaxIdLabel}
          name="stateTaxId"
          type="number"
          maxlength={DEFAULT_LENGTH_VALUE}
          className="mb-3"
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          required={isStateTaxIdRequired}
          rules={{
            required: {
              value: isStateTaxIdRequired,
              message: t('ERROR_STATE_TAX_ID_REQUIRED')
            },
            pattern: {
              value: INVALID_TAX_FIELD_CHARS_REGEX,
              message: replaceTokensInString(
                t('ADD_INV_CHAR_NEW_FIELD_WARNING', {
                  1: NAME_ERROR
                })
              )
            },
            validate: handleTaxIDValidation
          }}
        />
      )}
      {isNeighborhoodEnabled && (
        <TaxModalInput
          label={neighborhoodLabel}
          name="neighborhood"
          type="text"
          maxlength={40}
          className="mb-3"
          required={isNeighborhoodRequired}
          rules={{
            required: {
              value: isNeighborhoodRequired,
              message: t('ERROR_Neighborhood')
            },
            pattern: {
              value: INVALID_TAX_FIELD_CHARS_REGEX,
              message: replaceTokensInString(
                t('ADD_INV_CHAR_NEW_FIELD_WARNING', {
                  1: NAME_ERROR
                })
              )
            }
          }}
        />
      )}
    </>
  );
};
